"use client";

import { useEffect, useRef, useState } from "react";
import PhotowallDemoWall, { DEFAULT_DEMO_WALL_SETTINGS, type DemoWallSettings } from "./PhotowallDemoWall";
import { usePhotowallDemoFeed } from "./usePhotowallDemoFeed";

export type DemoUploadPattern = "none" | "trickle" | "burst" | "showcase";

interface UploadRound {
  atMs: number;
  count: number;
  spreadMs: number;
}

interface UploadRhythm {
  /** Rounds played once, the first time the wall is on screen, unless the visitor prefers reduced motion. */
  opening?: UploadRound[];
  /** When the regular rounds start, counted from the start of the opening. */
  afterOpeningMs?: number;
  /** Wait before the first upload when there's no opening to play. */
  firstMs: number;
  /** Time between upload rounds; each round varies a little. */
  everyMs: number;
  count: number;
  spreadMs: number;
}

const UPLOAD_RHYTHMS: Record<Exclude<DemoUploadPattern, "none">, UploadRhythm> = {
  trickle: { firstMs: 1500, everyMs: 6500, count: 1, spreadMs: 0 },
  burst: { firstMs: 2500, everyMs: 17000, count: 12, spreadMs: 5000 },
  // Catches a visitor scrolling past: a burst a second after the wall shows,
  // then the trickle.
  showcase: {
    opening: [{ atMs: 1000, count: 12, spreadMs: 1000 }],
    afterOpeningMs: 11000,
    firstMs: 1500,
    everyMs: 6500,
    count: 1,
    spreadMs: 0,
  },
};

interface PhotowallLoopingWallProps {
  label: string;
  settings?: Partial<DemoWallSettings>;
  uploads?: DemoUploadPattern;
  isWaiting?: boolean;
  firstLayout?: number;
  className?: string;
}

/** A demo wall that simulates guests uploading on a steady rhythm while it's on screen. */
export default function PhotowallLoopingWall({
  label,
  settings,
  uploads = "none",
  isWaiting = false,
  firstLayout,
  className,
}: PhotowallLoopingWallProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const hasPlayedOpeningRef = useRef(false);
  const feed = usePhotowallDemoFeed();
  const { addUploads, cancelUploads } = feed;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting), {
      threshold: 0.4,
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (uploads === "none" || isWaiting || !isOnScreen) return;
    const rhythm = UPLOAD_RHYTHMS[uploads];
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const after = (delay: number, run: () => void): void => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        run();
      }, delay);
      timers.add(timer);
    };
    const schedule = (delay: number): void => {
      after(delay, () => {
        addUploads(rhythm.count, rhythm.spreadMs);
        schedule(rhythm.everyMs * (0.8 + Math.random() * 0.4));
      });
    };
    // An opening cut short by scrolling away plays again next time. A burst
    // of a dozen arrivals is skipped for visitors who prefer reduced motion.
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const opening =
      hasPlayedOpeningRef.current || prefersReducedMotion ? [] : rhythm.opening ?? [];
    opening.forEach((round) => {
      after(round.atMs, () => addUploads(round.count, round.spreadMs));
    });
    const lastRound = opening[opening.length - 1];
    if (lastRound) {
      after(lastRound.atMs + lastRound.spreadMs, () => {
        hasPlayedOpeningRef.current = true;
      });
    }
    schedule(opening.length > 0 ? rhythm.afterOpeningMs ?? rhythm.firstMs : rhythm.firstMs);
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      cancelUploads();
    };
  }, [uploads, isWaiting, isOnScreen, addUploads, cancelUploads]);

  return (
    <div ref={containerRef} className={className}>
      <PhotowallDemoWall
        images={feed.images}
        settings={{ ...DEFAULT_DEMO_WALL_SETTINGS, ...settings }}
        isWaiting={isWaiting}
        firstLayout={firstLayout}
        label={label}
      />
    </div>
  );
}
